import { StandardDialog } from '@roc/ui';
import { useLoanStore } from '@roc/feature-loans';
import { observer } from 'mobx-react';
import { CustomTaskDialog, useGeneralTasksStore } from '@roc/feature-general-tasks';
import Prescreen from './prescreen';
import { useEffect, useMemo } from 'react';
import { LOAN_PRESCREEN_EXCEPTION_TEXT, LOAN_PRESCREEN_TEXT, SENIOR_PRE_REVIEW_PRESCREEN_STATUS } from '../../../utils/documentConstants';

export type PrescreenDialogProps = {
  loanId: number;
  taskId?: number;
  open?: boolean;
  onClose?: () => void;
};

export const PrescreenDialog = observer((props: PrescreenDialogProps) => {
  const { loanId, taskId, open, onClose } = props;

  const { loanStore } = useLoanStore();
  const { generalTasksStore } = useGeneralTasksStore();
  const prescreenData = loanStore.loanPrescreenData;
  const isManualQuote = Boolean(loanStore?.loanDetails?.manualQuoteLoanDetailValues?.manualQuote);

  useEffect(() => {
    if (!open) return;
    if (loanId == null) return;

    loanStore.fetchLoanPrescreenData(loanId);
  }, [open, loanId, loanStore]);

  const readOnly = useMemo(() => {
    if (!prescreenData) return false;

    const hasMessage =
      typeof prescreenData.prescreenMessageResponse === 'string' &&
      prescreenData.prescreenMessageResponse.trim().length > 0;

    return hasMessage;
  }, [prescreenData]);

  const sendPrescreenMessages = async (prescreenMessage: string, exceptionMessage: string) => {
    await loanStore.sendPrescreenMessage(loanId, prescreenMessage);

    const hasExceptionLeverage = !!prescreenData?.exceptionLeverageRequest;

    if (hasExceptionLeverage && !isManualQuote) {
      const exceptionResponse = prescreenData.exceptionLeverageRequest + '\n\nResponse:\n' + (exceptionMessage ?? '');
      await loanStore.sendPrescreenMessage(loanId, exceptionResponse.split('\n').join('<br/>'));
    }
  }

  const refreshLoanPrescreen = async (prescreenMessage: string, exceptionMessage: string) => {
    const prescreenText = prescreenMessage || null;
    const exceptionText = isManualQuote ? null : (exceptionMessage || null);

    await loanStore.updatePrescreenMessage(loanId, prescreenText, exceptionText);
    await loanStore.patchLoanCustomFields(loanId, { prescreen: true });
    await loanStore.fetchLoanPrescreenData(loanId);
  }

  const refreshTaskStore = async () => {
    if (taskId) {
      await generalTasksStore.markTaskAsCompletedV2(taskId);
      await generalTasksStore.applyFilters();
    }
  }

  const handleSubmit = async (prescreenMessage: string, exceptionMessage: string) => {
    try {
      await sendPrescreenMessages(prescreenMessage, exceptionMessage);
      await refreshLoanPrescreen(prescreenMessage, exceptionMessage);
      await refreshTaskStore();

      generalTasksStore.globalStore.notificationStore.showSuccessNotification({
        message: "Prescreen sent successfully.",
      });

      window.parent.postMessage({ action: 'prescreen-submitted', payload: { loanId } }, '*');
      onClose?.();
    } catch (error) {
      generalTasksStore.globalStore.notificationStore.showErrorNotification({
        message: "Failed to submit prescreen.",
      });
    }
  };

  const content = (
    <Prescreen
      readOnly={readOnly}
      prescreenMessageResponse={prescreenData?.prescreenMessageResponse || LOAN_PRESCREEN_TEXT}
      exceptionLeverageRequest={prescreenData?.exceptionLeverageRequest || null}
      exceptionLeverageResponse={prescreenData?.exceptionLeverageResponse || LOAN_PRESCREEN_EXCEPTION_TEXT}
      isManualQuote={isManualQuote}
      onSubmit={handleSubmit}
      onCancel={onClose}
    />
  );

  return taskId ? (
    <CustomTaskDialog
      open={open}
      title="Prescreen"
      handleClose={onClose}
      dialogContent={content}
      taskId={taskId}
    />
  ) : (
    <StandardDialog
      open={open}
      title="Prescreen"
      maxWidth="sm"
      handleClose={onClose}
      dialogContent={content}
    />
  );
});

export default PrescreenDialog;
